'use client';
import type { NextPage } from 'next';
import { useMemo, type CSSProperties } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './button.module.scss';
import { ButtonType } from '@/types/footer.types';

const FooterButton: NextPage<ButtonType> = ({
  className = '',
  color = 'accent',
  highContrast = false,
  loading = false,
  size = 1,
  state = 'default',
  variant = 'primary',
  text = '',
  iconStart = false,
  iconEnd = false,
  focusRing = false,
  buttonHeight,
  buttonWidth,
  onClick,
  border1 = false,
  href,
}) => {
  const buttonStyle: CSSProperties = useMemo(() => {
    return {
      height: buttonHeight,
      width: buttonWidth,
      ...(border1 && { border: '1px solid #272B30' }),
    };
  }, [buttonHeight, buttonWidth, border1]);

  const ButtonContent = () => (
    <>
      {!!focusRing && (
        <div className={styles.focusRing}>
          <div className={styles.border2} />
          <div className={styles.border1} />
        </div>
      )}
      {!!iconStart && (
        <Image
          className={styles.designBlendingMode}
          width={18}
          height={18}
          sizes="100vw"
          alt=""
          src="/design--blendingmode.svg"
        />
      )}
      <div className={styles.button}>{text}</div>
      {!!iconEnd && (
        <Image
          className={styles.designBlendingMode1}
          width={18}
          height={18}
          sizes="100vw"
          alt=""
          src="/design--blendingmode.svg"
        />
      )}
    </>
  );

  const commonProps = {
    className: [styles.button1, className].join(' '),
    'data-color': color,
    'data-highcontrast': highContrast,
    'data-loading': loading,
    'data-size': size,
    'data-state': state,
    'data-variant': variant,
    'data-focus-ring': focusRing,
    'data-border1': border1,
    style: buttonStyle,
  };

  if (href) {
    return (
      <Link href={href} {...commonProps} passHref>
        <ButtonContent />
      </Link>
    );
  }

  return (
    <button {...commonProps} onClick={onClick}>
      <ButtonContent />
    </button>
  );
};

export default FooterButton;
